import { clearPreferredMerchantId, setActiveMerchantId } from "@/lib/api";
import { auth } from "@/lib/firebase";
import { captureAuthEvent } from "@/lib/sentry";

export type SignOutReason = "session-expired" | "refresh-failed" | "user-initiated" | "remote-tab";

const VALID_REASONS: ReadonlySet<SignOutReason> = new Set<SignOutReason>([
  "session-expired",
  "refresh-failed",
  "user-initiated",
  "remote-tab",
]);

export function isSignOutReason(value: unknown): value is SignOutReason {
  return typeof value === "string" && (VALID_REASONS as ReadonlySet<string>).has(value);
}

const BROADCAST_CHANNEL_NAME = "auth-events";

interface SignedOutMessage {
  type: "signed-out";
  reason?: SignOutReason;
  at: number;
}

let channel: BroadcastChannel | null = null;

function getChannel(): BroadcastChannel | null {
  if (typeof window === "undefined" || typeof BroadcastChannel === "undefined") {
    return null;
  }
  if (!channel) {
    channel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
  }
  return channel;
}

/**
 * Subscribe to cross-tab auth events. The handler fires when *another* tab
 * signs out so this tab can hard-redirect to /login immediately rather than
 * waiting for the next route transition.
 */
export function subscribeAuthBroadcast(onSignedOut: (msg: SignedOutMessage) => void): () => void {
  const ch = getChannel();
  if (!ch) return () => {};
  const handler = (e: MessageEvent<SignedOutMessage>) => {
    if (e.data?.type === "signed-out") onSignedOut(e.data);
  };
  ch.addEventListener("message", handler);
  return () => ch.removeEventListener("message", handler);
}

function broadcastSignedOut(reason?: SignOutReason): void {
  const ch = getChannel();
  if (!ch) return;
  const msg: SignedOutMessage = { type: "signed-out", reason, at: Date.now() };
  try {
    ch.postMessage(msg);
  } catch {
    // BroadcastChannel can throw `DataCloneError` in extreme cases; ignore.
  }
}

// Set once this tab starts leaving for /login. Every sign-out drops the
// Firebase user, and the session watcher must not answer that with its own
// sign-out (its reason would replace this one on the login page). Never a gate
// on handleSignOut itself: a "Leave site?" prompt can cancel the navigation,
// and Sign out has to work when clicked again.
let leavingForLogin = false;

export function isLeavingForLogin(): boolean {
  return leavingForLogin;
}

/**
 * Hard-redirect to `/login` without touching Firebase. For when the session
 * is already gone (another tab signed out, Firebase dropped the user): a
 * second `signOut()` would turn a momentary null into a real sign-out, while
 * a bare redirect self-heals because the login page sends a signed-in user
 * straight back.
 */
export function goToLogin(reason?: SignOutReason): void {
  if (typeof window === "undefined") return;
  leavingForLogin = true;
  const url = new URL("/login", window.location.origin);
  if (reason) url.searchParams.set("reason", reason);
  window.location.href = url.pathname + url.search;
}

/**
 * Sign out of Firebase and hard-redirect to `/login`. A hard navigation is
 * deliberate: it drops any in-memory state (stores, caches, timers) so the
 * next session starts clean. Optional `reason` surfaces an inline banner on
 * the login page so the user knows why they were kicked back.
 *
 * Also broadcasts to other open tabs so they redirect at the same time
 * instead of lingering on a stale, signed-out app shell.
 */
export async function handleSignOut(reason?: SignOutReason): Promise<void> {
  if (typeof window === "undefined") return;
  leavingForLogin = true;
  captureAuthEvent("sign-out", { reason });
  broadcastSignedOut(reason);
  // Drop both merchant pointers (session + persisted preference) so the
  // next signed-in user on the same device doesn't inherit either.
  setActiveMerchantId(null);
  clearPreferredMerchantId();
  await auth.signOut().catch(() => {});
  goToLogin(reason);
}
